import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Repeat, CalendarDays } from 'lucide-react';
import { AREE } from '../../constants/areas';
import Checkbox from '../nav/Checkbox';
import PromoteToMitButton from '../nav/PromoteToMitButton';
import DateAssignPopover from '../nav/DateAssignPopover';
import TaskTextEditor from '../nav/TaskTextEditor';
import MovedBadge from '../nav/MovedBadge';
import { formatSettimanaConRange } from '../../lib/weekFormat';
import { labelSettimana } from '../../lib/periodFormat';

const FADE_TRANS = 'opacity 280ms cubic-bezier(0.22, 1, 0.36, 1)';

/**
 * AzioniSection — lista delle azioni settimanali raggruppate per area (T3).
 * Ogni riga include un PromoteToMitButton per spostare l'azione in MIT.
 *
 * Props:
 *   - filteredAzioni, azioni, filtersActive
 *   - filteredAzioniByArea : [[areaKey, Azione[]], ...]
 *   - krById
 *   - isCollapsed, toggleCollapsed
 *   - onToggle(id, next), onChipAreaClick(area), onPromote()
 *   - onEditRecurrence(parent) — apre RecurrenceEditor
 *   - onAssignDate(azioneId, currentValue, newValue) — assegna data specifica
 *     o settimana flottante a un'azione. Se omesso, il pulsante non appare.
 */
export default function AzioniSection({
  filteredAzioni, azioni, filtersActive,
  filteredAzioniByArea,
  krById,
  isCollapsed, toggleCollapsed,
  onToggle, onChipAreaClick, onPromote, onEditRecurrence, onAssignDate,
  onUpdateText, onDeleteTask,
}) {
  if (filteredAzioni.length === 0) return null;

  const groupByArea = true;
  const groups = filteredAzioniByArea;

  return (
    <section className="mb-12" data-testid="section-azioni">
      <h2 className="font-display text-xl md:text-2xl tracking-tight mb-4">
        Azioni · {filteredAzioni.length}
        {filtersActive && azioni.length > filteredAzioni.length ? ` di ${azioni.length}` : ''}
      </h2>

      {groups.map(([key, items]) => {
        const done = items.filter((a) => a.fatto).length;
        const pct = items.length > 0 ? Math.round((done / items.length) * 100) : 0;
        const areaMeta = groupByArea
          ? (AREE[key] || { color: 'var(--qp-text-soft)', label: key === 'senza-area' ? 'Senza area' : key })
          : null;
        const headerLabel = groupByArea
          ? (areaMeta.label || (key === 'senza-area' ? 'Senza area' : key))
          : (key === 'senza-settimana' ? 'Senza settimana' : labelSettimana(key));
        const showHeader = groupByArea || groups.length > 1;

        return (
          <div
            key={key}
            className="mb-5"
            data-testid={`azione-group-${key}`}
            style={groupByArea ? { borderTop: `3px solid ${areaMeta.color}`, paddingTop: 10 } : undefined}
          >
            {showHeader && (
              <button
                type="button"
                onClick={() => toggleCollapsed('azione', key)}
                className="w-full flex items-baseline gap-3 mb-2 pb-1 border-b border-line hover:bg-linesoft/40 transition-colors text-left"
                data-testid={`azione-group-header-${key}`}
                aria-expanded={!isCollapsed('azione', key)}
              >
                <span className="shrink-0 text-mute" aria-hidden>
                  {isCollapsed('azione', key)
                    ? <ChevronRight size={12} strokeWidth={1.5} />
                    : <ChevronDown size={12} strokeWidth={1.5} />}
                </span>
                <span
                  className="font-mono text-[11px] uppercase tracking-[0.2em]"
                  style={{ color: groupByArea ? areaMeta.color : '#171717' }}
                >
                  {headerLabel}
                </span>
                <span className="font-mono text-[10px] text-soft tabular-nums" data-testid={`azione-group-stat-${key}`}>
                  {done}/{items.length} · {pct}%
                </span>
                <span className="flex-1 h-1 bg-linesoft relative overflow-hidden ml-1" aria-hidden="true">
                  <span
                    className="absolute inset-y-0 left-0 transition-all"
                    style={{
                      width: `${pct}%`,
                      backgroundColor: pct === 100 ? 'var(--qp-success)' : (groupByArea ? areaMeta.color : 'var(--qp-ink)'),
                    }}
                  />
                </span>
              </button>
            )}
            {!isCollapsed('azione', key) && (
              <ul className="divide-y divide-linesoft">
                {items.map((a) => {
                  const kr = a.key_result_id ? krById[a.key_result_id] : null;
                  const areaKey = (a.area || kr?.area || '').toLowerCase();
                  const meta = AREE[areaKey] || { color: 'var(--qp-text-soft)', label: '—' };
                  const textStyle = {
                    textDecoration: a.fatto ? 'line-through' : 'none',
                    color: a.fatto ? 'var(--qp-text-mute)' : undefined,
                    opacity: a.fatto ? 0.5 : 1,
                    transition: FADE_TRANS,
                  };
                  return (
                    <li key={a.id} className="group py-3 flex items-center gap-3" data-testid={`row-azione-${a.id}`}>
                      <Checkbox
                        checked={a.fatto}
                        onToggle={(next) => onToggle(a.id, next, a)}
                        size={18}
                        label={a.testo}
                        testid={`toggle-azione-${a.id}`}
                      />
                      <MovedBadge id={a.id} testid={`moved-badge-azione-${a.id}`} />
                      {(a.recurrence_rule || a.is_occurrence) ? (
                        <button
                          type="button"
                          onClick={() => onEditRecurrence && onEditRecurrence({
                            id: a.parent_id || a.id.split('::')[0],
                            kind: 'azione',
                            testo: a.testo,
                            recurrence_rule: a.recurrence_rule,
                            recurrence_end: a.recurrence_end,
                          })}
                          className="shrink-0 inline-flex items-center justify-center w-5 h-5 border border-ink bg-ink text-canvas hover:bg-ok hover:border-ok transition-colors"
                          title={a.is_occurrence ? 'Occorrenza ricorrente · modifica serie' : 'Modifica ricorrenza'}
                          aria-label="Modifica ricorrenza"
                          data-testid={`azione-recurrence-btn-${a.id}`}
                        >
                          <Repeat size={10} strokeWidth={1.8} />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onEditRecurrence && onEditRecurrence({
                            id: a.id,
                            kind: 'azione',
                            testo: a.testo,
                            recurrence_rule: null,
                            recurrence_end: null,
                          })}
                          className="shrink-0 inline-flex items-center justify-center w-5 h-5 border border-transparent text-mute hover:border-line hover:text-ink opacity-0 group-hover:opacity-100 transition-all"
                          title="Rendi ricorrente"
                          aria-label="Rendi ricorrente"
                          data-testid={`azione-recurrence-add-${a.id}`}
                        >
                          <Repeat size={10} strokeWidth={1.5} />
                        </button>
                      )}
                      {!groupByArea && (
                        <button
                          type="button"
                          onClick={() => areaKey && onChipAreaClick(areaKey)}
                          className="font-mono text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 border shrink-0 underline decoration-dotted underline-offset-2 hover:no-underline hover:opacity-70 cursor-pointer"
                          style={{ borderColor: meta.color, color: meta.color }}
                          title={areaKey ? `Filtra per ${meta.label}` : ''}
                          data-testid={`periodo-azione-area-chip-${a.id}`}
                        >
                          {a.is_bau ? 'BAU' : (meta.label || a.area || '—')}
                        </button>
                      )}
                      {onUpdateText ? (
                        <TaskTextEditor
                          kind="azione"
                          id={a.id}
                          isOccurrence={!!a.is_occurrence}
                          text={a.testo}
                          onSaveText={(nv) => onUpdateText('azione', a.id, nv)}
                          onDelete={onDeleteTask ? () => onDeleteTask('azione', a.id) : null}
                          textStyle={textStyle}
                        />
                      ) : (
                        <span
                          className="flex-1 font-body text-sm"
                          style={textStyle}
                        >
                          {a.testo}
                        </span>
                      )}
                      {kr && (
                        <Link
                          to={`/obiettivo/${kr.obiettivo_id}`}
                          className="font-mono text-[10px] uppercase tracking-[0.15em] text-soft hover:text-accent shrink-0"
                          data-testid={`azione-kr-link-${a.id}`}
                        >
                          → {kr.titolo}
                        </Link>
                      )}
                      {!groupByArea && groups.length === 1 && a.settimana && !a.data && (
                        <span className="font-mono text-[10px] tabular-nums text-mute shrink-0" title={a.settimana}>
                          {formatSettimanaConRange(a.settimana)}
                        </span>
                      )}
                      {/* Pillola data SEMPRE visibile per leggere quando è prevista l'azione.
                          - Se ha `data` specifica: pillola scura "lun 30 giu" o simile
                          - Altrimenti: pillola grigia con label settimana "W27" */}
                      {(a.data || a.settimana) && (
                        <span
                          className={`font-mono text-[10px] tabular-nums shrink-0 px-1.5 py-0.5 border ${
                            a.data
                              ? 'border-ink text-ink bg-canvas'
                              : 'border-line text-mute'
                          }`}
                          title={a.data ? `Fissata al ${a.data}` : `Settimana ${a.settimana}`}
                          data-testid={`azione-date-label-${a.id}`}
                        >
                          {a.data ? formatDateShort(a.data) : (a.settimana || '').replace('20', '').replace('-W', ' · W')}
                        </span>
                      )}
                      {onAssignDate && !a.is_occurrence && (
                        <DateAssignPopover
                          kind="azione"
                          currentValue={a.data || a.settimana || ''}
                          onSave={(nv) => onAssignDate(a.id, a.data || a.settimana || '', nv)}
                          side="left"
                          testid={`azione-date-popover-${a.id}`}
                        >
                          <button
                            type="button"
                            className={`shrink-0 inline-flex items-center justify-center w-5 h-5 border transition-colors ${
                              a.data
                                ? 'border-ink text-ink hover:bg-ink hover:text-canvas'
                                : 'border-line text-mute hover:border-ink hover:text-ink'
                            }`}
                            title={a.data
                              ? `Azione fissata al ${a.data} · clicca per cambiare`
                              : 'Assegna data specifica o sposta settimana'}
                            aria-label="Assegna data"
                            data-testid={`azione-date-btn-${a.id}`}
                          >
                            <CalendarDays size={11} strokeWidth={1.6} />
                          </button>
                        </DateAssignPopover>
                      )}
                      <PromoteToMitButton
                        azioneId={a.id}
                        onPromoted={onPromote}
                        testid={`periodo-promote-mit-${a.id}`}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </section>
  );
}

/**
 * Formatta una data YYYY-MM-DD come "dom 5 lug" (ITA, abbreviato).
 * Fallback al raw se parsing fallisce.
 */
function formatDateShort(iso) {
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' });
  } catch {
    return iso;
  }
}

